import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReportingPageLayout from './ReportingPageLayout';
import KpiCardGrid from './KpiCardGrid';
import RevenueAndChannelSection from './RevenueAndChannelSection';
import useReportingPage from '../hooks/useReportingPage';
import { currencyFormatter, compactCurrency, getGranularity } from '../utils/reportingUtils';

const axios = require('axios').default;

function ReportingDashboardPage({ team, csrfToken, currentUser, venueOwnerships }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters, comparison,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage('month_to_date');

  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);

  const [revenueTrend, setRevenueTrend] = useState(null);
  const [revenueTrendLoading, setRevenueTrendLoading] = useState(true);

  const [eventsByVenue, setEventsByVenue] = useState([]);
  const [eventsByVenueLoading, setEventsByVenueLoading] = useState(true);

  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingEventsLoading, setUpcomingEventsLoading] = useState(true);
  const [upcomingEventsTotal, setUpcomingEventsTotal] = useState(0);

  const [bookingKpis, setBookingKpis] = useState(null);
  const [bookingKpisLoading, setBookingKpisLoading] = useState(true);
  const [bookingDateMode, setBookingDateMode] = useState('event_date');

  const filterGroups = useMemo(() => {
    if (!venueOwnerships || venueOwnerships.length === 0) return [];
    return [
      {
        label: 'Venues',
        filters: [
          {
            id: 'venue_ownership_id',
            label: 'Venue',
            type: 'checkbox',
            options: venueOwnerships.map((vo) => ({
              value: String(vo.id),
              label: vo.name || vo.venue_name || `Venue ${vo.id}`,
            })),
          },
        ],
      },
    ];
  }, [venueOwnerships]);

  const buildApiParams = useCallback(() => {
    const params = {};
    if (dateRange && dateRange.start) params.start_date = dateRange.start;
    if (dateRange && dateRange.end) params.end_date = dateRange.end;
    params.granularity = getGranularity(dateRange);
    if (comparison.start) params.prior_start_date = comparison.start;
    if (comparison.end) params.prior_end_date = comparison.end;
    if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
      params['venue_ownership_id[]'] = activeFilters.venue_ownership_id;
    }
    return params;
  }, [dateRange, activeFilters, comparison]);

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    setKpisLoading(true);
    const params = buildApiParams();
    axios.get(`/teams/${team.id}/reporting/dashboard_kpis.json`, { params })
      .then((res) => setKpis(res.data || null))
      .catch(() => {})
      .finally(() => setKpisLoading(false));
  }, [team && team.id, csrfToken, dateRange, activeFilters]);

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    setRevenueTrendLoading(true);
    const params = buildApiParams();

    axios.get(`/teams/${team.id}/reporting/revenue_trend.json`, { params })
      .then((res) => setRevenueTrend(res.data || null))
      .catch(() => {})
      .finally(() => setRevenueTrendLoading(false));

    setEventsByVenueLoading(true);
    axios.get(`/teams/${team.id}/reporting/events_by_venue.json`, { params })
      .then((res) => setEventsByVenue(res.data || []))
      .catch(() => {})
      .finally(() => setEventsByVenueLoading(false));
  }, [team && team.id, csrfToken, dateRange, activeFilters]);

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    setBookingKpisLoading(true);
    const params = buildApiParams();
    if (bookingDateMode === 'event_date') params.date_mode = 'event_date';
    axios.get(`/teams/${team.id}/reporting/booking_kpis.json`, { params })
      .then((res) => setBookingKpis(res.data || null))
      .catch(() => {})
      .finally(() => setBookingKpisLoading(false));
  }, [team && team.id, csrfToken, dateRange, activeFilters, bookingDateMode]);

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchUpcoming = async () => {
      try {
        setUpcomingEventsLoading(true);
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const params = {
          page: 1,
          per_page: 200,
          sort_direction: 'asc',
        };
        if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
          params['venue_ownership_id[]'] = activeFilters.venue_ownership_id;
        }

        const response = await axios.get(`/teams/${team.id}/reporting/upcoming_events.json`, { params });
        const responseData = response.data || {};
        setUpcomingEvents(responseData.events || []);
        setUpcomingEventsTotal(responseData.pagination?.total_entries || 0);
      } catch (_e) {
      } finally {
        setUpcomingEventsLoading(false);
      }
    };

    fetchUpcoming();
  }, [team && team.id, csrfToken, activeFilters]);

  const k = kpis || {};
  const prior = k.prior || {};
  const cmpLabel = comparison.label;

  const bk = bookingKpis || {};
  const bkPrior = bk.prior || {};
  const bookingKpiCards = [
    {
      top: { label: 'Holds', value: (bk.holds || 0).toLocaleString(), current: bk.holds, prior: bkPrior.holds, tooltip: 'Hold events in the selected period', href: `/teams/${team.id}/reporting/booking/holds` },
      bottom: { label: 'Unique Artists w/ Holds', value: (bk.unique_artists_with_holds || 0).toLocaleString(), current: bk.unique_artists_with_holds, prior: bkPrior.unique_artists_with_holds, tooltip: 'Distinct artists performing at hold events' },
    },
    {
      top: { label: 'Offers', value: (bk.offers || 0).toLocaleString(), current: bk.offers, prior: bkPrior.offers, tooltip: 'Total offers created in the selected period', href: `/teams/${team.id}/reporting/booking/offers` },
      bottom: { label: 'Unique Artists w/ Offers', value: (bk.unique_artists_with_offers || 0).toLocaleString(), current: bk.unique_artists_with_offers, prior: bkPrior.unique_artists_with_offers, tooltip: 'Distinct artists linked to offers' },
    },
    {
      top: { label: 'Confirms', value: (bk.confirms || 0).toLocaleString(), current: bk.confirms, prior: bkPrior.confirms, tooltip: 'Confirmed events in the selected period' },
      bottom: { label: 'Unique Artists w/ Confirms', value: (bk.unique_artists_with_confirms || 0).toLocaleString(), current: bk.unique_artists_with_confirms, prior: bkPrior.unique_artists_with_confirms, tooltip: 'Distinct artists performing at confirmed events' },
    },
  ];

  const kpiCards = [
    {
      top: { label: 'All Events', value: (k.all_events || 0).toLocaleString(), current: k.all_events, prior: prior.all_events, tooltip: 'Total confirmed events in the selected period' },
      bottom: { label: 'Ticketed Events', value: (k.ticketed_events || 0).toLocaleString(), current: k.ticketed_events, prior: prior.ticketed_events, tooltip: 'Confirmed events with at least one ticket type' },
    },
    {
      top: { label: 'Announced', value: (k.announced || 0).toLocaleString(), current: k.announced, prior: prior.announced, tooltip: 'Confirmed events that are on-sale, pre-sale, or past' },
      bottom: { label: 'On-Sale', value: (k.on_sale || 0).toLocaleString(), current: k.on_sale, prior: prior.on_sale, tooltip: 'Events currently on sale' },
    },
    {
      top: { label: 'Tickets Sold', value: (k.tickets_sold || 0).toLocaleString(), current: k.tickets_sold, prior: prior.tickets_sold, tooltip: 'Total admission tickets sold' },
      bottom: { label: 'Ticket Revenue', value: compactCurrency(k.gross_tickets_revenue || 0), current: k.gross_tickets_revenue, prior: prior.gross_tickets_revenue, tooltip: 'Net ticket revenue to organizer after Opendate fees. Complimentary tickets contribute $0.' },
    },
    {
      top: { label: 'Add-Ons Sold', value: (k.addons_sold || 0).toLocaleString(), current: k.addons_sold, prior: prior.addons_sold, tooltip: 'Total add-on items sold' },
      bottom: { label: 'Gross Add-Ons $ Sold', value: compactCurrency(k.gross_addons_revenue || 0), current: k.gross_addons_revenue, prior: prior.gross_addons_revenue, tooltip: 'Total gross add-on revenue' },
    },
    {
      top: { label: 'PHAB', value: currencyFormatter.format(k.phab || 0), current: k.phab, prior: prior.phab, tooltip: 'Per Head Average Beverage (F&B revenue / tickets sold)' },
      bottom: { label: 'F&B Revenue', value: compactCurrency(k.fnb_revenue || 0), current: k.fnb_revenue, prior: prior.fnb_revenue, tooltip: 'Food & beverage revenue from events' },
    },
  ];

  return (
    <ReportingPageLayout
      team={team}
      activeKey="dashboard"
      title="Dashboard"
      subtitle="High-level view of your revenue, ticket sales, and events."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={filterGroups}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      filterTitle="Filter Dashboard"
      filterSubtitle="Apply filters to narrow dashboard data."
    >
      <KpiCardGrid
        loading={kpisLoading}
        isMobile={isMobile}
        columns={5}
        cards={kpiCards}
        comparisonLabel={cmpLabel}
      />

      <RevenueAndChannelSection
        revenueTrend={revenueTrend}
        revenueTrendLoading={revenueTrendLoading}
        comparisonLabel={cmpLabel}
        eventsByVenue={eventsByVenue}
        eventsByVenueLoading={eventsByVenueLoading}
        upcomingEvents={upcomingEvents}
        upcomingEventsLoading={upcomingEventsLoading}
        upcomingEventsTotal={upcomingEventsTotal}
        bookingKpiCards={bookingKpiCards}
        bookingKpisLoading={bookingKpisLoading}
        bookingDateMode={bookingDateMode}
        setBookingDateMode={setBookingDateMode}
        teamId={team && team.id}
        isMobile={isMobile}
        dateRange={dateRange}
      />
    </ReportingPageLayout>
  );
}

export default ReportingDashboardPage;
